/* eslint-disable max-lines */
import { PrivacyCenterTrackingPurposePreview } from '@main/cm-types';
import {
  FlexRowCenterVertical,
  Form,
  formatErrorMessage,
  message,
  Prompt,
  Skeleton,
} from '@main/core-ui';
import { endpoints } from '@main/pc-types';
import { RequiredEnabledAirgap } from '@main/privacy-center/src/components';
import {
  StyledPrimaryButton,
  StyledSecondaryButton,
} from '@main/privacy-center/src/components/Card/wrappers';
import { StyledColumnContainer } from '@main/privacy-center/src/components/SectionWrapper/wrappers';
import {
  selectCoreIdentifier,
  selectSessionProfile,
} from '@main/privacy-center/src/containers/App/selectors';
import { sp } from '@main/theme';
import { LanguageKey } from '@transcend-io/internationalization';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { selectActiveDataSubject } from '../../containers/ActiveDataSubject/selectors';
import {
  buildUseDhMutation,
  useChildOrganizationPreferences,
  useConsentPreferences,
  usePartition,
} from '../../hooks';
import {
  getSetAllPreferencesInput,
  getUpdateDataSubjectConsentPreferencesInput,
  transformPurposesToFormPurposes,
} from '../helpers';
import {
  selectConsentIdentifiers,
  selectPurposePreferences,
} from '../ManageYourPrivacy/selectors';
import {
  setPrivacyCenterPurposePreferences,
  setPrivacyCenterUserIdentifiers,
} from '../ManageYourPrivacy/slice';
import { ChildOrganizationPreferencesForm } from './ChildOrganizationPreferencesForm';
import {
  EmailPreviewWithUnsubscribe,
  Purpose,
  PurposeListForm,
} from './components';
import { ChildOrganizationHeader } from './components/ChildOrganizationHeader';
import { communicationsPreferencesMessages } from './messages';
import { OrganizationFormController } from './types';

const useDataSubjectUpdateConsentPreferences = buildUseDhMutation(
  endpoints.dataSubjectUpdateConsentPreferences,
);

interface UpdatePreferencesFormProps {
  /** Configured Purposes */
  purposes: PrivacyCenterTrackingPurposePreview[];
}

/**
 * Form Component for updating user's communication preferences
 */
export const UpdatePreferencesForm: React.FC<
  React.PropsWithChildren<UpdatePreferencesFormProps>
> = ({ purposes: configuredPurposes }) => {
  // Hook to retrieve child organizations and the user's preferences for those orgs
  const { childOrganizations, isDraft, organization, logo } =
    useChildOrganizationPreferences();
  const { locale, formatMessage } = useIntl();
  const [{ partitionOptions, selectedPartition }] = usePartition();
  const coreIdentifier = useSelector(selectCoreIdentifier);
  const sessionProfile = useSelector(selectSessionProfile);
  const activeDataSubject = useSelector(selectActiveDataSubject);
  const [unsubscribeAll, setUnsubscribeAll] = useState(false);
  const [computedPurposes, setComputedPurposes] = useState<
    Record<string, Purpose>
  >({});
  const identifiers = useSelector(selectConsentIdentifiers);
  const [isFormReady, setIsFormReady] = useState(false);
  const dispatch = useDispatch();
  const userPreferences = useSelector(selectPurposePreferences);

  // State to store form state/actions for child organizations
  const [registeredActions, setRegisteredActions] = useState<
    Record<string, OrganizationFormController>
  >({});

  const {
    data: preferenceStoreRecord,
    decryptedIdentifiers,
    error: fetchConsentPreferencesError,
  } = useConsentPreferences(
    !coreIdentifier || !selectedPartition,
    selectedPartition,
    coreIdentifier?.value,
  );

  const [updateDataSubjectConsentPreferences, { loading, error }] =
    useDataSubjectUpdateConsentPreferences({
      refetchQueries: [
        'DataSubjectConsentPreferences',
        'RequestHistory',
        'RequestHistoryActiveCount',
      ],
      awaitRefetchQueries: true,
    });

  const configuredCommunicationPurposes = useMemo(
    () =>
      configuredPurposes.filter(
        ({ showInPrivacyCenter }) => showInPrivacyCenter,
      ),
    [configuredPurposes],
  );

  // Form method with default values for parent organization
  const formMethods = useForm({
    shouldUnregister: false,
    defaultValues: {
      email: coreIdentifier?.value ?? '',
      purposes: {} as Record<string, Purpose>,
    },
  });

  // Watch for changes in form values
  const formPurposes = formMethods.watch('purposes');

  // Effect to show error message if any
  useEffect(() => {
    if (error || fetchConsentPreferencesError) {
      message.error(
        formatErrorMessage(
          error?.message || fetchConsentPreferencesError?.message || '',
        ),
      );
    }
  }, [error, fetchConsentPreferencesError]);

  // Update redux store and localStorage with user's preferences and identifiers
  useEffect(() => {
    if (preferenceStoreRecord && selectedPartition) {
      dispatch(
        setPrivacyCenterPurposePreferences({
          partition: selectedPartition,
          preferences: preferenceStoreRecord.purposes ?? [],
        }),
      );
    }
    if (decryptedIdentifiers && selectedPartition) {
      dispatch(
        setPrivacyCenterUserIdentifiers({
          partition: selectedPartition,
          identifiers: decryptedIdentifiers ?? [],
        }),
      );
    }
  }, [
    preferenceStoreRecord,
    decryptedIdentifiers,
    selectedPartition,
    dispatch,
  ]);

  // Effect to update the unsubscribeAll state when all purposes are selected
  useEffect(() => {
    if (configuredCommunicationPurposes) {
      const unsubscribe = configuredCommunicationPurposes.every(
        ({ trackingType }) => formPurposes[trackingType]?.consent === true,
      );
      setUnsubscribeAll(unsubscribe);
    }
  }, [formPurposes, configuredCommunicationPurposes]);

  // Effect to set the default values in the form from the API response
  useEffect(() => {
    if (selectedPartition && userPreferences[selectedPartition]) {
      const purposes = userPreferences[selectedPartition];

      const defaultFormPurposes = transformPurposesToFormPurposes(
        purposes,
        configuredCommunicationPurposes,
      );

      formMethods.reset({ purposes: defaultFormPurposes });
      setIsFormReady(true);
      setComputedPurposes(defaultFormPurposes);
    }
  }, [
    formMethods,
    configuredCommunicationPurposes,
    userPreferences,
    selectedPartition,
  ]);

  /**
   * On form submit, updates communication preferences
   *
   * @param data - form data
   */
  const handleSubmit = async (data: {
    /** email address */
    email: string;
    /** consent selections */
    purposes: Record<string, Purpose>;
  }): Promise<void> => {
    if (
      activeDataSubject &&
      coreIdentifier &&
      selectedPartition &&
      updateDataSubjectConsentPreferences
    ) {
      const { input: updatedRow, dhEncryptedInput } =
        getUpdateDataSubjectConsentPreferencesInput(
          coreIdentifier.value,
          identifiers[selectedPartition] ?? [],
          selectedPartition,
          data.purposes,
        );
      // Submit the child organizations
      Object.values(registeredActions).forEach(({ submitForm }) =>
        submitForm(),
      );

      // Submit the parent organization
      await updateDataSubjectConsentPreferences({
        variables: {
          input: {
            consentPreferences: [updatedRow],
            locale: locale as LanguageKey,
            subjectType: activeDataSubject.type,
          },
        },
        dhEncryptedInput,
      }).then(({ data: updatedResponse }) => {
        if (updatedResponse) {
          message.success(
            formatMessage(communicationsPreferencesMessages.success),
          );
        }
      });
    }
  };

  /**
   * Opt out/In of all  preferences
   */
  function setAllPreferences(): void {
    const latestSelections = getSetAllPreferencesInput(
      !unsubscribeAll,
      configuredCommunicationPurposes,
    );
    formMethods.setValue('purposes', latestSelections, { shouldDirty: true });
    Object.values(registeredActions).forEach(({ setAllValues }) =>
      setAllValues(!unsubscribeAll),
    );
    setUnsubscribeAll(!unsubscribeAll);
  }

  /**
   *  Reset all preferences to initial state
   */
  function resetPreferences(): void {
    formMethods.reset({ purposes: computedPurposes });
    Object.values(registeredActions).forEach(({ resetValues }) =>
      resetValues(),
    );
  }

  // cannot render without a partition
  if (partitionOptions.length === 0) {
    return <RequiredEnabledAirgap />;
  }

  // Render skeleton while loading
  if (
    !isFormReady ||
    !selectedPartition ||
    (configuredCommunicationPurposes.length === 0 &&
      childOrganizations.length === 0)
  ) {
    return <Skeleton />;
  }

  return (
    <>
      <Prompt
        when={formMethods.formState.isDirty}
        message={formatMessage(
          communicationsPreferencesMessages.unconfirmedChanges,
        )}
      />
      <Form
        useFormMethods={formMethods}
        onSubmit={(data) => {
          handleSubmit(data);
        }}
      >
        <StyledColumnContainer style={{ gap: sp(4) }}>
          <EmailPreviewWithUnsubscribe
            onClick={setAllPreferences}
            unsubscribeAll={unsubscribeAll}
            identifier={sessionProfile?.email ?? sessionProfile?.nickname}
            showUnsubscribeAll={
              configuredCommunicationPurposes.length > 1 ||
              childOrganizations.length > 0
            }
          />
          {childOrganizations.length > 0 ? (
            configuredCommunicationPurposes.length > 0 ? (
              <div>
                <ChildOrganizationHeader logo={logo} name={organization.name} />
                <div style={{ padding: '15px' }}>
                  <PurposeListForm
                    name="purposes"
                    purposes={configuredCommunicationPurposes}
                  />
                </div>
              </div>
            ) : null
          ) : (
            <PurposeListForm
              name="purposes"
              purposes={configuredCommunicationPurposes}
            />
          )}
          {childOrganizations
            .filter((org) => org.purposes)
            .map((org) => (
              <ChildOrganizationPreferencesForm
                key={org.id}
                childOrganization={org}
                activeDataSubject={activeDataSubject}
                registerAction={(val) => {
                  setRegisteredActions((curr) => ({
                    ...curr,
                    [val.id]: val,
                  }));
                }}
                unregisterAction={(id) => {
                  setRegisteredActions((curr) => {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const { [id]: _, ...rest } = curr;
                    return rest;
                  });
                }}
                coreIdentifier={coreIdentifier?.value}
                isDraft={isDraft}
              />
            ))}
          <FlexRowCenterVertical style={{ gap: sp(1), marginLeft: 'auto' }}>
            <StyledSecondaryButton
              type="reset"
              buttonType="secondary"
              loading={loading}
              disabled={
                !formMethods.formState.isDirty &&
                Object.values(registeredActions).every(
                  ({ isDirty }) => !isDirty,
                )
              }
              onClick={() => resetPreferences()}
            >
              {formatMessage(
                communicationsPreferencesMessages.resetPreferences,
              )}
            </StyledSecondaryButton>
            <StyledPrimaryButton
              type="submit"
              buttonType="primary"
              loading={loading}
              disabled={
                !formMethods.formState.isDirty &&
                Object.values(registeredActions).every(
                  ({ isDirty }) => !isDirty,
                )
              }
            >
              {formatMessage(
                communicationsPreferencesMessages.updatePreferences,
              )}
            </StyledPrimaryButton>
          </FlexRowCenterVertical>
        </StyledColumnContainer>
      </Form>
    </>
  );
};
/* eslint-enable max-lines */
